import React from 'react';
import { clsx } from 'clsx';
import { ArrowPathIcon, DocumentTextIcon, GlobeAltIcon, TrashIcon } from '@heroicons/react/24/outline';
import { KnowledgeSource } from '../../types';
import StatusChip from './StatusChip';
import { formatFileSize, formatRelative } from './format';

interface SourceRowProps {
  source: KnowledgeSource;
  /** Start time of the running sync, so file rows can show elapsed time too. */
  indexingSince?: string;
  onStageDelete: (source: KnowledgeSource) => void;
  onUndoDelete: (source: KnowledgeSource) => void;
  onRecrawl: (source: KnowledgeSource) => void;
  /** Opens the page list. Absent for files, which have no pages to list. */
  onShowPages?: (source: KnowledgeSource) => void;
  /** Opens the refusal detail. Only websites are ever refused. */
  onShowRejection?: (source: KnowledgeSource) => void;
}

export default function SourceRow({
  source,
  indexingSince,
  onStageDelete,
  onUndoDelete,
  onRecrawl,
  onShowPages,
  onShowRejection,
}: SourceRowProps) {
  const isRemoving = source.status === 'removing';
  const isWebsite = source.kind === 'website';
  const Icon = isWebsite ? GlobeAltIcon : DocumentTextIcon;

  // The two refusal states, which `status` tells apart (F11).
  //
  // A first crawl that was refused is terminal and empty: there is no data source
  // left, so there is nothing to refresh and nothing to list pages from. Delete is
  // the only action, and the row is kept only so its reason and breakdown survive.
  const wasRejected = source.status === 'rejected';
  // A refused REFRESH is not a broken row. The site is still indexed and still
  // answering from the previous copy — so it keeps its Indexed chip and its page
  // link, and only carries a warning. It stays RETRYABLE: refresh used to be
  // disabled here, on the reasoning that repeating it unchanged reaches the same
  // verdict, but nothing except a successful refresh ever cleared the reason — so
  // the customer who freed up space had no way back at all (F13). A refresh that
  // merely FAILED carries no reason and never disabled anything, which is the
  // distinction the backend records and this flag reads.
  const refreshRefused = !wasRejected && !!source.website?.rejectionReason;

  // What the crawl kept, against what it reached. The two differ constantly and the
  // gap used to be invisible here: a section-scoped crawl of one site found 42 URLs
  // and added 1, and this column said "1 page" with nothing to suggest the other 41
  // existed — while the panel behind it listed all 42 as though they were indexed.
  //
  // Shown as "1 of 42" only when they actually differ, so an ordinary site keeps an
  // ordinary page count. A number that visibly does not add up is also the strongest
  // invitation to open the list there is; it is doing double duty on purpose.
  const indexed = source.website?.pagesIndexed;
  const discovered = source.website?.pagesDiscovered;
  const withheld =
    isWebsite && indexed !== undefined && discovered !== undefined && discovered > indexed
      ? discovered - indexed
      : 0;

  const measure = isWebsite
    ? source.pages !== undefined
      ? withheld > 0
        ? `${indexed} of ${discovered} pages`
        : `${source.pages} ${source.pages === 1 ? 'page' : 'pages'}`
      : ''
    : source.size !== undefined
      ? formatFileSize(source.size)
      : '';

  // Whether there is a page list to open. `checking` qualifies: the site IS indexed
  // underneath and its pages are exactly as listable as a minute earlier.
  const canShowPages =
    isWebsite && !!onShowPages && (source.status === 'indexed' || source.status === 'checking');

  // The current read's start, NOT when the site was added (C11) — otherwise a
  // re-crawl's counter carries on from whenever the URL was first typed and never
  // restarts. `addedAt` remains the fallback for rows written before the API
  // returned `crawlStartedAt`; on a first crawl the two are seconds apart anyway.
  const since = isWebsite
    ? source.website?.crawlStartedAt || source.website?.addedAt
    : indexingSince;

  return (
    // The whole row opens the page list, not just the count in it. The count alone was
    // undiscoverable by construction: it sits in a right-hand column styled exactly
    // like the two static columns beside it, and the only thing marking it as a control
    // was an underline on hover. Deliberately NOT the site name — with a globe icon and
    // the URL beneath it, the name reads as "open this website", and hijacking it to a
    // slide-over trades one wrong expectation for another.
    //
    // A plain onClick on the div rather than role="button": the row already contains
    // buttons and links, and nesting those inside a widget role is invalid. The count
    // button below stays the keyboard and screen-reader route to the same panel.
    <div
      onClick={canShowPages ? () => onShowPages!(source) : undefined}
      className={clsx(
        'group flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-slate-700/60 last:border-b-0 transition-colors',
        isRemoving ? 'bg-red-50/50 dark:bg-red-900/10' : 'hover:bg-gray-50 dark:hover:bg-slate-700/30',
        canShowPages && 'cursor-pointer'
      )}
    >
      <Icon
        className={clsx(
          'h-5 w-5 flex-shrink-0',
          isRemoving ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'
        )}
      />

      <div className="flex-1 min-w-0">
        <div
          className={clsx(
            'text-sm font-medium truncate',
            isRemoving ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-gray-100'
          )}
          title={source.detail || source.name}
        >
          {source.name}
        </div>
        {isWebsite && source.detail && (
          <div className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{source.detail}</div>
        )}
        {source.status === 'uploading' && source.progress !== undefined && (
          <div className="mt-1.5 h-1 w-full max-w-[220px] rounded-full bg-gray-200 dark:bg-slate-600 overflow-hidden">
            <div
              className="h-full bg-gray-900 dark:bg-white transition-all duration-200"
              style={{ width: `${source.progress}%` }}
            />
          </div>
        )}
        {source.status === 'failed' && source.errorMessage && (
          <div className="text-xs text-red-600 dark:text-red-400 truncate mt-0.5">{source.errorMessage}</div>
        )}

        {/* A refusal always says why, inline. The chip only has room for "Not
            added", and a terminal state with no reason on screen is the exact
            failure this replaced. */}
        {wasRejected && (
          <div className="mt-0.5 flex items-baseline gap-1.5 min-w-0">
            <span className="text-xs text-red-600 dark:text-red-400 truncate">
              {source.errorMessage || 'This website could not be added.'}
            </span>
            {onShowRejection && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  onShowRejection(source);
                }}
                className="text-xs text-red-600 dark:text-red-400 underline hover:no-underline flex-shrink-0"
              >
                Why?
              </button>
            )}
          </div>
        )}

        {/* Amber, not red: nothing is broken. The site is answering; only the
            attempt to update it was declined. */}
        {refreshRefused && (
          <div className="mt-0.5 flex items-baseline gap-1.5 min-w-0">
            <span className="text-xs text-amber-700 dark:text-amber-400 truncate">
              Could not be updated — the earlier version is still in use.
            </span>
            {onShowRejection && (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  onShowRejection(source);
                }}
                className="text-xs text-amber-700 dark:text-amber-400 underline hover:no-underline flex-shrink-0"
              >
                Why?
              </button>
            )}
          </div>
        )}
      </div>

      {measure && (
        <div className="flex-shrink-0 text-xs tabular-nums text-gray-400 dark:text-gray-500 w-28 text-right">
          {/* C1: the page count becomes the way in to "which 85 pages?". A link rather
              than a separate control because the count is already the thing being
              questioned when an answer looks wrong. Only for a site that finished
              indexing — there is nothing to list before that. */}
          {/* `checking` keeps the link: the site IS indexed underneath and its pages
              are exactly as listable as a minute earlier. Dropping it would make the
              count go dead for three minutes for no reason the customer can see. */}
          {canShowPages ? (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onShowPages!(source);
              }}
              // Underlined AT REST, and a step darker than the columns either side of
              // it. This cell used to be `text-gray-400` with `hover:underline` — the
              // same class as the "2m ago" beside it and the same one a file's size
              // uses, which is not a control at all. Nothing distinguished the one
              // cell in this column that does something.
              // The shortfall is the number itself. A second line reading "41 not
              // added" was tried and removed: 42 minus 1 is arithmetic the reader
              // should not be asked to check, and "1 of 42" already looks odd enough
              // to invite the click — which the whole row now accepts anyway.
              title={
                withheld > 0
                  ? `${indexed} of ${discovered} pages found were added — see which`
                  : 'See which pages we read'
              }
              className="underline decoration-dotted underline-offset-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:decoration-solid"
            >
              {measure}
            </button>
          ) : (
            measure
          )}
        </div>
      )}

      <div className="flex-shrink-0 w-32 flex justify-start">
        <StatusChip status={source.status} since={since} errorMessage={source.errorMessage} />
      </div>

      <div className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 w-20 text-right hidden sm:block">
        {formatRelative(source.updatedAt)}
      </div>

      <div className="flex-shrink-0 flex items-center gap-1 w-16 justify-end">
        {isRemoving ? (
          <button
            onClick={e => {
              e.stopPropagation();
              onUndoDelete(source);
            }}
            className="px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 rounded transition-colors"
          >
            Undo
          </button>
        ) : (
          <>
            {/* Hidden outright on a rejected row rather than disabled: there is no
                data source behind it, so the route answers 400 and a greyed button
                would only advertise something that cannot work. */}
            {isWebsite && !wasRejected && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onRecrawl(source);
                }}
                // `checking` disables it too, and that is not cosmetic (C13). The row
                // still carries `green_data_source_id` in that window, so the recrawl
                // route answers 409 "already being re-read" — against a chip that
                // used to read a calm green "Indexed" with nothing else on screen.
                // F13 un-disabled this button on a refusal, which made the window
                // something customers would actually press into.
                disabled={source.status === 'indexing' || source.status === 'checking'}
                // A refused refresh stays RETRYABLE (F13). Disabling it made the
                // refusal permanent: nothing cleared the reason except a successful
                // refresh, so a customer refused for being over their storage limit
                // could free up any amount of space and still never re-read their
                // site. The amber line beside it already carries the news, and the
                // route now re-judges the account before it crawls anything — so the
                // button's job is to let them act on what the line told them.
                title={
                  refreshRefused
                    ? 'Try reading this site again — see why the last update was declined'
                    : 'Read this site again'
                }
                className={clsx(
                  'p-1.5 rounded-full transition-colors focus:opacity-100',
                  // Kept permanently visible rather than revealed on hover, so the
                  // way out of a refusal is on screen next to the reason for it.
                  // Stated as whole alternatives — two opacity utilities on one
                  // element resolve by stylesheet order, not by the order they are
                  // written here.
                  refreshRefused
                    ? 'opacity-100 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200'
                    : 'opacity-0 group-hover:opacity-100 text-gray-300 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-0'
                )}
              >
                <ArrowPathIcon className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={e => {
                e.stopPropagation();
                onStageDelete(source);
              }}
              disabled={source.status === 'uploading'}
              title={isWebsite ? 'Remove this website' : 'Remove this document'}
              className="p-1.5 text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 rounded-full transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-30"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
